// Single source of truth for GrowthOS — full 20-slide deck.
// Each slide has a `layout` that drives the renderer in GrowthOSPage.

export const BRAIN_LABELS = [
  "Smart Marketer IP",
  "Frameworks",
  "Courses",
  "SOPs",
  "Playbooks",
  "Best Practices",
];

const BRAND_LOGO_BASE =
  "https://gary-command-center.preview.static.emergentagent.com/brand";
const BRAND_SUB_AGENTS = ["Marketing", "Email & SMS", "Content", "Analytics"];

export const BRANDS = [
  {
    id: "boom",
    name: "BOOM! by Cindy Joseph",
    short: "BOOM!",
    logo: `${BRAND_LOGO_BASE}/boom.png`,
    tagline: "Pro-age Beauty",
    accent: "#9CC6F0",
    accentSoft: "rgba(156,198,240, 0.45)",
    subAgents: BRAND_SUB_AGENTS,
  },
  {
    id: "young-again",
    name: "Young Again",
    short: "Young Again",
    logo: `${BRAND_LOGO_BASE}/young-again.png`,
    tagline: "Health & Wellness",
    accent: "#4690D9",
    accentSoft: "rgba(70,144,217, 0.45)",
    subAgents: BRAND_SUB_AGENTS,
  },
  {
    id: "overtone",
    name: "Overtone",
    short: "Overtone",
    logo: `${BRAND_LOGO_BASE}/overtone.png`,
    tagline: "Hair Color",
    accent: "#34D399",
    accentSoft: "rgba(52, 211, 153, 0.45)",
    subAgents: BRAND_SUB_AGENTS,
  },
];

export const BRAND_KNOWLEDGE_NODES = [
  "Brand Voice",
  "Products",
  "Customers",
  "Past Campaigns",
  "Internal SOPs",
  "Workflows",
  "Goals",
];

export const PLATFORMS = [
  { id: "shopify", name: "Shopify", short: "Shopify", logo: "https://cdn.simpleicons.org/shopify/95BF47" },
  { id: "klaviyo", name: "Klaviyo", short: "Klaviyo", logo: "https://www.google.com/s2/favicons?domain=klaviyo.com&sz=64" },
  { id: "meta", name: "Meta Ads", short: "Meta", logo: "https://cdn.simpleicons.org/meta/0866FF" },
  { id: "google", name: "Google Ads", short: "Google Ads", logo: "https://cdn.simpleicons.org/googleads/4285F4" },
  { id: "ga4", name: "GA4", short: "GA4", logo: "https://cdn.simpleicons.org/googleanalytics/E37400" },
  { id: "merchant", name: "Merchant Center", short: "Merchant", logo: "https://cdn.simpleicons.org/google/4285F4" },
  { id: "omnisend", name: "Omnisend", short: "Omnisend", logo: "https://www.google.com/s2/favicons?domain=omnisend.com&sz=64" },
  { id: "gworkspace", name: "Google Workspace", short: "Workspace", logo: "https://cdn.simpleicons.org/google/4285F4" },
];

export const AI_TOOLS = [
  { id: "chatgpt", name: "ChatGPT", note: "Conversational AI" },
  { id: "claude", name: "Claude", note: "Reasoning & writing" },
  { id: "claude-code", name: "Claude Code", note: "Engineering" },
  { id: "claude-design", name: "Claude Design", note: "Design" },
  { id: "codex", name: "Codex", note: "Code generation" },
  { id: "gemini", name: "Gemini", note: "Multimodal" },
  { id: "nano-banana", name: "Nano Banana", note: "Image generation" },
];

export const AI_APPS = [
  { id: "lovable", name: "Lovable", note: "App builder" },
  { id: "emergent", name: "Emergent", note: "Full-stack agent" },
  { id: "bolt", name: "Bolt", note: "Web builder" },
  { id: "higgsfield", name: "Higgsfield", note: "Video / motion" },
  { id: "adcreative-ai", name: "Adcreative.ai", note: "Creative ops" },
];

export const TEAM_PROFICIENCIES = [
  { person: "Person A", tool: "Claude Code", color: "#4690D9" },
  { person: "Person B", tool: "ChatGPT", color: "#9CC6F0" },
  { person: "Person C", tool: "Lovable", color: "#34D399" },
  { person: "Person D", tool: "Klaviyo", color: "#F59E0B" },
  { person: "Person E", tool: "Meta Ads", color: "#E11D48" },
];

export const WORKFLOW_STEPS = [
  { step: "Business Goal", note: "Operator gives the objective" },
  { step: "Brand Agent", note: "Plans the path" },
  { step: "Brain + Brand Memory", note: "Pulls strategy & context" },
  { step: "Tools & Apps", note: "Selects what's needed" },
  { step: "Platform Execution", note: "Shopify · Klaviyo · Meta · Google" },
  { step: "QA & Approval", note: "Human checkpoint" },
  { step: "Launch", note: "Work ships" },
  { step: "Reporting + Skill", note: "Learns & saves it" },
];

export const SKILL_LOOP = [
  "Task completed",
  "Learnings captured",
  "Reusable skill created",
  "Faster, better next time",
];

export const SUB_AGENT_NAMES = [
  "Email & SMS",
  "Paid Media",
  "Creative",
  "Landing Pages",
  "Shopify Ops",
  "Analytics",
  "Customer Research",
  "Offer Strategy",
];

export const PRIVATE_BRAND_ENVS = BRANDS.map((b) => ({
  id: b.id,
  short: b.short,
  logo: b.logo,
  accent: b.accent,
  accentSoft: b.accentSoft,
  blocks: ["NVIDIA Server", "Private Memory", "Storage", "Permissions"],
}));

export const BUSINESS_BENEFITS = [
  "Less dependence on individual AI expertise",
  "Faster execution",
  "More consistent output",
  "Better use of internal knowledge",
  "Compounding skills & memory",
  "Better coordination across platforms",
  "More leverage for the team",
  "Strategy connected to execution",
];

export const STATS = [
  { value: "$125M+", label: "Brand revenue scaled" },
  { value: "15 yrs", label: "Operator playbooks" },
  { value: "60K+", label: "Founders in the ecosystem" },
];

export const CHANNELS = [
  {
    id: "slack",
    name: "Slack",
    logo: "https://www.google.com/s2/favicons?domain=slack.com&sz=128",
    accent: "#ECB22E",
  },
  {
    id: "teams",
    name: "Microsoft Teams",
    short: "Teams",
    logo: "https://www.google.com/s2/favicons?domain=teams.microsoft.com&sz=128",
    accent: "#6264A7",
  },
  {
    id: "telegram",
    name: "Telegram",
    logo: "https://cdn.simpleicons.org/telegram/26A5E4",
    accent: "#26A5E4",
  },
  {
    id: "sms",
    name: "SMS · iMessage",
    short: "SMS",
    logo: "https://cdn.simpleicons.org/imessage/34DA50",
    accent: "#34DA50",
  },
  {
    id: "email",
    name: "Email",
    logo: "https://cdn.simpleicons.org/gmail/EA4335",
    accent: "#EA4335",
  },
];

// ============================================================================
// SLIDES (20)
// ============================================================================
export const SLIDES = [
  {
    id: 1,
    layout: "title",
    eyebrow: "Smart Marketer · GrowthOS",
    title: "GrowthOS",
    subtitle: "The AI operating layer for e-commerce execution.",
    body: [
      "A trained AI operator for your brand — one that can use the tools, access the brain, connect to your systems, and execute the work.",
    ],
    quote:
      "This isn't another AI chatbot. This is the operating layer that turns AI into coordinated business execution.",
  },
  {
    id: 2,
    layout: "chipgrid",
    eyebrow: "The Landscape",
    title: "Your Team Has More AI Tools Than Ever",
    items: AI_TOOLS,
    body: [
      "ChatGPT, Claude, Gemini, Codex, Nano Banana — your team is surrounded by powerful models.",
      "Each tool is useful. Each one needs a human who knows which to use, how to prompt it, and how to move the output into the business.",
    ],
    footnote: "The tools are powerful. But the business still depends on the person operating them.",
    quote:
      "There's never been more AI in front of your team. The question isn't whether they have AI. It's whether AI is actually running work.",
  },
  {
    id: 3,
    layout: "chipgrid",
    eyebrow: "The Landscape",
    title: "And More AI Apps Are Shipping Every Week",
    items: AI_APPS,
    body: [
      "Lovable, Emergent, Bolt, Higgsfield, Adcreative.ai — point solutions that can create assets, sites, and workflows quickly.",
      "They're powerful — but each one is its own island. They don't know your brand, your goals, your stack, or each other.",
    ],
    footnote: "AI tools and apps can create outputs. GrowthOS coordinates execution across the business.",
    quote:
      "These apps create. They don't coordinate. Without a layer above them, the team becomes the integration.",
  },
  {
    id: 4,
    layout: "silos",
    eyebrow: "The Problem",
    title: "AI Tools Create New Silos",
    silos: [
      {
        team: "Email Marketing Team",
        color: "#9CC6F0",
        tools: [
          { name: "Klaviyo", logo: "https://www.google.com/s2/favicons?domain=klaviyo.com&sz=64" },
          { name: "ChatGPT", logo: "https://www.google.com/s2/favicons?domain=openai.com&sz=64" },
          { name: "Codex", logo: "https://www.google.com/s2/favicons?domain=openai.com&sz=64" },
        ],
        memoryIn: "ChatGPT",
      },
      {
        team: "Advertising Team",
        color: "#4690D9",
        tools: [
          { name: "Meta Ads", logo: "https://cdn.simpleicons.org/meta/0866FF" },
          { name: "Triple Whale", logo: "https://www.google.com/s2/favicons?domain=triplewhale.com&sz=64" },
          { name: "Claude", logo: "https://www.google.com/s2/favicons?domain=claude.ai&sz=64" },
        ],
        memoryIn: "Claude",
      },
    ],
    footnote:
      "E-commerce is already a complex tech stack. Layering disconnected AI tools on top just builds taller silos.",
    body: [
      "Your email team runs Klaviyo with ChatGPT & Codex. Your ads team runs Meta & Triple Whale with Claude.",
      "Everything each team does and discusses is trapped inside a different AI — so the two sides never share the same context.",
    ],
    quote:
      "Every new AI tool adds another silo. The work and the knowledge get locked inside whatever app each team happens to use.",
  },
  {
    id: 5,
    layout: "layeredstack",
    eyebrow: "Introducing GrowthOS",
    title: "GrowthOS Sits Above the Tools",
    body: [
      "The operator gives the goal. GrowthOS determines the path.",
      "It selects the right tool, pulls from The Brain, accesses brand memory, connects to your platforms, and executes.",
    ],
    footnote: "GrowthOS turns AI tools, business systems, and strategic knowledge into coordinated execution.",
    quote:
      "Think of this as the layer that finally makes all of it work together. Your team gives the goal. The system runs the path.",
  },
  {
    id: 6,
    layout: "workflow",
    eyebrow: "Goal to Execution",
    title: "Give the Goal. The Agent Handles the Path.",
    example: '"Build and launch a weekend promotion for our best-selling bundle."',
    body: [
      "Review past promos → pull offer strategy from The Brain → build the Shopify discount → create Klaviyo segments → write the email sequence → design the emails → schedule sends → build ad creative → launch → monitor → report → save learnings.",
    ],
    quote:
      "The agent doesn't just answer questions. It turns business goals into completed work — and reports back when it's done.",
  },
  {
    id: 7,
    layout: "skill-loop",
    eyebrow: "Compounding Capability",
    title: "The More It Works, the More Capable It Becomes",
    examples: [
      { title: "After a Klaviyo flow", learns: "Flow structure · segmentation · design standards · brand voice · QA process" },
      { title: "After a promotion", learns: "Offer structure · cadence · creative angles · Shopify setup · scheduling" },
      { title: "After Google Ads", learns: "Reporting format · optimization · budget logic · escalation triggers" },
    ],
    body: [
      "Every completed task can become a reusable skill — saved to the brand's private memory.",
      "Your Brand Agent isn't just executing. It's developing compounding institutional knowledge.",
    ],
    quote:
      "The more the agent works, the more capable it becomes inside your brand's environment. It's a flywheel, not a feature.",
  },
  {
    id: 8,
    layout: "brain",
    eyebrow: "Layer 01 — Intelligence",
    title: "The Brain",
    body: [
      "Smart Marketer's centralized knowledge base — courses, IP, transcripts, SOPs, frameworks, playbooks.",
      "Offer strategy. Paid media strategy. Email & SMS strategy. Landing pages. Funnels. Retention. Subscription. Customer research.",
      "Every agent in the ecosystem can pull from it.",
    ],
    quote:
      "Your Brand Agent doesn't start from scratch. It starts with the same strategic foundation Smart Marketer would use to train a high-performing operator.",
  },
  {
    id: 9,
    layout: "ip-value",
    eyebrow: "The Brain · Smart Marketer IP",
    title: "Your Agent Is Trained on the Entire Smart Marketer Library",
    categories: { course: "Smart Course", blueprint: "Blueprint", mini: "Mini-Course" },
    courses: [
      { name: "Smart Business Systems", price: "$1,997", cat: "course" },
      { name: "Smart Ecommerce", price: "$1,497", cat: "course" },
      { name: "Smart Paid Traffic", price: "$1,497", cat: "course" },
      { name: "Smart Email Marketing", price: "$1,497", cat: "course" },
      { name: "Smart Google Ads", price: "$1,497", cat: "course" },
      { name: "Smart Amazon Ecommerce", price: "$1,497", cat: "course" },
      { name: "Smart Social Marketing", price: "$1,497", cat: "course" },
      { name: "Smart Product Creation", price: "$1,497", cat: "course" },
      { name: "Smart Business Exit", price: "$1,497", cat: "course" },
      { name: "Google Performance Max", price: "$397", cat: "blueprint" },
      { name: "YouTube Kickstart", price: "$397", cat: "blueprint" },
      { name: "Ambassador Blueprint", price: "$397", cat: "blueprint" },
      { name: "Build a Podcast", price: "$397", cat: "blueprint" },
      { name: "Build a Perfect PDP", price: "$397", cat: "blueprint" },
      { name: "Ad Creative System", price: "$197", cat: "mini" },
    ],
    total: "$16,155",
    totalLabel: "Total catalog value",
    totalNote: "Included with GrowthOS — and your agent is trained on every course.",
    body: [
      "The Brain isn't generic knowledge — it's Smart Marketer's actual course library: 15 premium trainings worth $16,155.",
      "Your agent studies all of it, so every email, ad, funnel and promo it builds is grounded in proven Smart Marketer frameworks.",
    ],
    quote:
      "Buying these courses would cost over $16,000 — and you'd still have to study them. With GrowthOS, your agent already has.",
  },
  {
    id: 10,
    layout: "intelligence-sources",
    eyebrow: "How the Agent Decides",
    title: "Three Layers of Intelligence",
    sources: [
      {
        key: "memory",
        name: "Brand Memory",
        desc: "Your private brand context",
        icon: "database",
        color: "#4690D9",
        weight: 100,
        weightLabel: "Heaviest weight",
        items: ["Products & catalog", "Brand voice", "Customer & order data", "Campaign history"],
      },
      {
        key: "brain",
        name: "The Brain",
        desc: "Shared strategic knowledge",
        icon: "brain",
        color: "#9CC6F0",
        weight: 58,
        weightLabel: "Secondary weight",
        items: ["Smart Marketer IP", "Proven frameworks", "Playbooks & SOPs", "E-com best practices"],
      },
      {
        key: "internet",
        name: "The Internet",
        desc: "Connected to leading LLMs",
        icon: "globe",
        color: "#6B8BB5",
        weight: 26,
        weightLabel: "Lightest weight",
        items: ["Live trends & research", "Broad world knowledge", "Real-time context"],
      },
    ],
    footnote:
      "The agent weighs all three — your private brand context always carries the most weight.",
    body: [
      "The agent makes every decision by drawing on three sources: your private Brand Memory, the shared Brain, and the live internet through connected LLMs.",
      "They're weighted: the internet carries the least weight, the Brain more, and your Brand Memory the most — so the agent stays grounded in your specific business.",
    ],
    quote:
      "Brand Memory first, the Brain second, the internet last. Your private context always outweighs generic knowledge.",
  },
  {
    id: 11,
    layout: "gary",
    eyebrow: "Layer 02 — The Super Agent",
    title: "Gary Makes the Whole System Smarter",
    skill: "A faster way to build Shopify landing pages",
    tiers: { brain: "The Brain", gary: "Gary · Super Agent", agents: "Brand Agents" },
    caption: "Rising tides raise all ships.",
    privacyNote: "Gary shares techniques — never your private data or IP.",
    body: [
      "Gary sits between The Brain and every Brand Agent — the super agent that upholds the whole ecosystem.",
      "When one agent discovers a more efficient way to work — say, a faster way to build Shopify landing pages — Gary generalizes that technique, lifts it into The Brain, and makes it available to every brand on GrowthOS.",
      "Gary shares skills and techniques that make the system faster and more intuitive — never a brand's private data or IP.",
    ],
    quote:
      "Rising tides raise all ships. One agent gets smarter, and through Gary, every agent on GrowthOS does too.",
  },
  {
    id: 12,
    layout: "private-infra",
    eyebrow: "Layer 06 — Security & Isolation",
    title: "Security Is a First-Class Concern",
    securityBadges: [
      {
        id: "nemo",
        label: "NVIDIA NeMo Guardrails",
        note: "Prompt-injection defense, output filtering, policy enforcement at the model layer.",
        iconId: "shield",
      },
      {
        id: "server",
        label: "Dedicated NVIDIA Server",
        note: "Each brand runs on its own private compute — no shared inference, no cross-tenant bleed.",
        iconId: "server",
      },
      {
        id: "memory",
        label: "Encrypted Brand Memory",
        note: "Private memory store, encrypted at rest. Brand context never leaves the environment.",
        iconId: "lock",
      },
      {
        id: "permissions",
        label: "Permission Boundaries",
        note: "Scoped credentials per platform integration with explicit allowlists.",
        iconId: "key",
      },
    ],
    body: [
      "We take security seriously. Every Brand Agent runs on its own dedicated NVIDIA server, with isolated memory, isolated storage, and explicit permission boundaries.",
      "Every agent is equipped with NVIDIA NeMo Guardrails — defending against prompt injection, jailbreaks, and unsafe outputs at the model layer.",
    ],
    quote:
      "Each brand gets its own secure operating environment — dedicated server, encrypted memory, NeMo Guardrails on every request. Privacy, isolation, and prompt-injection defense, by default.",
  },
  {
    id: 13,
    layout: "hub-walkthrough",
    eyebrow: "The GrowthOS HUB",
    title: "Talk to Your Agent. Manage It in the HUB.",
    video: "/video/slack-bfcm.mp4",
    videoWebm: "/video/slack-bfcm.webm",
    slackCaption: "Work with your agent in Slack",
    hubImage: "/img/hub-dashboard.webp",
    hubImageAlt: "The GrowthOS HUB — a live dashboard to manage your Brand Agent, connections, skills and team",
    hubLink: "https://growthos-client-console-v2.pages.dev/",
    hubLinkDomain: "growthos-client-console-v2.pages.dev",
    hubCaption: "Manage everything in the GrowthOS HUB",
    body: [
      "On the left, your team simply talks to the agent in Slack — brief it, approve work, move on.",
      "In the HUB, you manage the agent itself: connections, skills, team, Alloy usage and everything it has learned — all in one place.",
    ],
    quote:
      "Conversation gets the work done. The HUB gives you full visibility and control over your agent.",
  },
  {
    id: 14,
    layout: "video-slide",
    eyebrow: "Proof · End-to-End",
    title: "From Slack Thread to Scheduled Revenue",
    video: "/video/slack-to-revenue.mp4",
    videoWebm: "/video/slack-to-revenue.webm",
    aspect: "16 / 9",
    body: [
      "A single Slack conversation turns into fully built, scheduled campaigns — strategy, copy, design, and setup handled by the agent.",
      "Brief in, scheduled revenue out.",
    ],
    quote:
      "From a Slack thread to scheduled revenue — the agent takes it the whole way.",
  },
  {
    id: 15,
    layout: "case-study",
    eyebrow: "Proof · Landing Pages",
    title: "From Brief to a Live Landing Page — Built in Shopify",
    image: "/img/case-landing-hero.webp",
    imageAlt: "Ancient Midnight Oil landing page designed and built by the Brand Agent, published live on Shopify",
    link: "https://www.ibdassist.com/pages/ancient-midnight-oil",
    linkDomain: "www.ibdassist.com/pages/ancient-midnight-oil",
    linkLabel: "Open the live landing page",
    placeholder: "Agent designing & publishing a conversion landing page directly in Shopify",
    body: [
      "The agent designed the layout, wrote the copy, generated the imagery, and built the full page directly into Shopify — then published it live.",
      "No page builder to learn, no dev ticket. Just a brief in, a real landing page out.",
    ],
    quote:
      "Click the screenshot to open the live page — designed, written, and shipped in Shopify by the Brand Agent.",
  },
  {
    id: 16,
    layout: "ad-creative",
    eyebrow: "Proof · Ad Creative",
    title: "Ad Creative Engineered to Convert",
    image: "/img/case-ad-creative.webp",
    imageAlt:
      "Twelve UndrDog Hemp Tee ad creatives generated by the agent across top-, middle-, and bottom-of-funnel stages",
    signals: [
      { label: "Top-performing Meta ads", note: "What's already converting" },
      { label: "Best-selling Shopify products", note: "What deserves the spotlight" },
      { label: "Winning hooks & angles", note: "Pulled from The Brain" },
      { label: "Audience & demographics", note: "Who each ad speaks to" },
    ],
    funnel: [
      { stage: "TOF", name: "Top of Funnel", note: "Stop the scroll · introduce the product", tiles: "01–04", color: "#4690D9" },
      { stage: "MOF", name: "Middle of Funnel", note: "Build the case · benefits & why different", tiles: "05–08", color: "#9CC6F0" },
      { stage: "BOF", name: "Bottom of Funnel", note: "Close with proof, features & the offer", tiles: "09–12", color: "#34D399" },
    ],
    body: [
      "The agent studies what's already converting on Meta and which products sell best on Shopify, then pairs that with proven creative principles from The Brain.",
      "It writes the hooks, designs the layouts, and generates the imagery tuned to each demographic — building a full library mapped to top-, middle-, and bottom-of-funnel intent.",
    ],
    quote:
      "It doesn't just make ads. It reads what's working, understands the funnel, and builds creative designed to convert at every stage.",
  },
  {
    id: 17,
    layout: "video-slide",
    eyebrow: "Proof · Google Ads",
    title: "Google Ads, Run by an Agent",
    video: "/video/google-ads-agent.mp4",
    videoWebm: "/video/google-ads-agent.webm",
    aspect: "16 / 9",
    body: [
      "The agent was given an in-depth Google Ads operating prompt, then managed and monitored campaigns autonomously.",
      "It optimizes within ROAS/ACOS guardrails and delivers a full report to the inbox every morning at 5 a.m.",
    ],
    quote:
      "Platform-specific operating processes, learned once and executed continuously.",
  },
  {
    id: 18,
    layout: "benefits",
    eyebrow: "What Changes",
    title: "What GrowthOS Changes for the Business",
    items: BUSINESS_BENEFITS,
    body: [
      "GrowthOS turns AI from a collection of tools into a coordinated execution system — and your team becomes the people setting direction, not the people operating fragmented apps.",
    ],
    quote:
      "This is the unlock: less dependence on individual AI expertise, faster execution, compounding memory, and a stronger connection between strategy and the work.",
  },
  {
    id: 19,
    layout: "pricing",
    eyebrow: "Investment · GrowthOS",
    title: "One Price. The Entire Operating Layer.",
    previous: {
      label: "Previously",
      items: [
        { k: "Platform", v: "$1,997/mo" },
        { k: "AI usage", v: "$750–$1,000/mo" },
      ],
    },
    setupFee: "$9,997",
    plans: [
      {
        id: "annual",
        name: "Paid Annually",
        price: "$1,997",
        cadence: "/mo",
        note: "billed annually",
        badge: "Best value",
        featured: true,
      },
      {
        id: "monthly",
        name: "Month-to-Month",
        price: "$2,497",
        cadence: "/mo",
        note: "billed monthly",
        featured: false,
      },
    ],
    usageNote:
      "AI usage billed separately — reduced by Alloy from $750–$1,000 to ~$400–$500/mo.",
    valueStack: [
      {
        iconId: "agent",
        title: "Dedicated Custom Brand Agent",
        note: "A private AI operator built and trained around your brand.",
      },
      {
        iconId: "team",
        title: "Dedicated GO Team — GrowthOS Operator",
        note: "Manages your agent, keeps it up to date, and helps your team build the systems & connections it needs.",
      },
      {
        iconId: "alloy",
        title: "Alloy — Smart Model Routing",
        note: "Custom routing across OpenAI & Anthropic that cuts AI usage to ~$400–$500/mo.",
      },
      {
        iconId: "brain",
        title: "Full Smart Marketer Brain Access",
        note: "Every framework, SOP, and playbook — on tap for your agent.",
      },
      {
        iconId: "training",
        title: "Weekly / Bi-Weekly Training Calls",
        note: "Recurring live sessions with your dedicated GO Team member.",
      },
      {
        iconId: "owned",
        title: "The Agent Is Yours",
        note: "Leave anytime — we off-board the agent to you to keep. Brain, GO Team & support access end.",
      },
    ],
    body: [
      "A one-time $9,997 setup fee, then $1,997/mo billed annually or $2,497/mo month-to-month — with AI usage billed separately and cut by Alloy to ~$400–$500/mo.",
      "Every plan includes a dedicated Brand Agent, your GO Team, weekly/bi-weekly training calls, Alloy cost-optimized usage, and full Brain access — and the agent is always yours to keep.",
    ],
    quote:
      "One transparent price for a dedicated agent, a human GO Team, and the full Smart Marketer Brain — and the agent is always yours.",
  },
];
